import { useEffect, useMemo, useState } from 'react';
import {
  App,
  Button,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { GetAccessToken } from '../../utils/auth-utils';
import { scopes } from '../../authConfig';
import {
  createAutodialerBucket,
  deleteAutodialerBucket,
  getAutodialerBuckets,
  updateAutodialerBucket,
} from '../../services/admin-page-service';
import type {
  AutodialerBucketConfiguration,
  AutodialerBucketConfigurationRequest,
  UploadFrequency,
} from '../../dto/admin-config-models';
import { SalesChannel } from '../../dto/model';
import { Header } from '../header/header';

const { Title } = Typography;

const SALES_CHANNEL_OPTIONS = Object.values(SalesChannel).map((v) => ({ label: v, value: v }));

const PRIORITY_VALUES = [
  41, 42, 43, 44, 45, 46, 47, 48, 49, 50,
  51, 52, 53, 54, 55, 56, 57, 58, 59, 60,
  61, 62, 63, 64, 65, 66, 68, 69, 70, 71,
  72, 73, 74, 75, 76, 77, 78, 79, 80, 81,
  82, 83, 84, 85, 86, 87, 88, 89, 90, 91,
  92, 93, 94, 95, 96, 97, 98, 99,
];
const PRIORITY_OPTIONS = PRIORITY_VALUES.map((v) => ({ label: String(v), value: v }));

const UPLOAD_FREQUENCY_OPTIONS: { label: string; value: UploadFrequency }[] = [
  { label: 'None', value: 'None' },
  { label: 'Hourly', value: 'Hourly' },
  { label: 'Real Time', value: 'RealTime' },
  { label: 'Manual', value: 'Manual' }
];

const OPPORTUNITY_STATUS_OPTIONS = [
  { label: 'First Time Scheduling', value: 'FirstTimeScheduling' },
  { label: 'Rescheduling Needed', value: 'ReschedulingNeeded' },
];

interface FormValues {
  name: string;
  description?: string;
  autodialerBucketId: string;
  autodialerBucketName: string;
  salesChannels: string[];
  priorities: number[];
  uploadFrequency: UploadFrequency;
  opportunityStatus?: string;
  isActive: boolean;
}

const AutodialerBucketsConfig = () => {
  const { notification } = App.useApp();
  const { instance, inProgress } = useMsal();
  const [form] = Form.useForm<FormValues>();

  const [buckets, setBuckets] = useState<AutodialerBucketConfiguration[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingBucket, setEditingBucket] = useState<AutodialerBucketConfiguration | null>(null);
  const [saving, setSaving] = useState(false);

  const selectedChannels: string[] = Form.useWatch('salesChannels', form) ?? [];
  const isActiveForm: boolean = Form.useWatch('isActive', form) ?? false;
  const watchedBucketName: string = Form.useWatch('autodialerBucketName', form) ?? '';
  const uploadFrequencyWatcher: UploadFrequency = Form.useWatch('uploadFrequency', form) ?? 'None';

  const takenPriorities = useMemo(() => {
    if (!isActiveForm) return new Set<number>();
    const taken = new Set<number>();
    for (const b of buckets) {
      if (!b.isActive) continue;
      if (editingBucket && b.id === editingBucket.id) continue;
      if (b.salesChannels.some((ch) => selectedChannels.includes(ch))) {
        b.priorities.forEach((p) => taken.add(p));
      }
    }
    return taken;
  }, [buckets, selectedChannels, editingBucket, isActiveForm]);

  const priorityOptions = useMemo(
    () => PRIORITY_OPTIONS.map((o) => ({ ...o, disabled: takenPriorities.has(o.value) })),
    [takenPriorities],
  );

  const load = async () => {
    try {
      setLoading(true);
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const data = await getAutodialerBuckets(token.accessToken);
      if (data) {
        setBuckets(data);
      } else {
        notification.error({ message: 'Failed to load autodialer buckets' });
      }
    } catch (e) {
      notification.error({ message: 'Failed to load autodialer buckets', description: String(e) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [instance, inProgress]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setEditingBucket(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true, uploadFrequency: 'None', salesChannels: [], priorities: [] });
    setDrawerOpen(true);
  };

  const openEdit = (bucket: AutodialerBucketConfiguration) => {
    setEditingBucket(bucket);
    form.setFieldsValue({
      name: bucket.name,
      description: bucket.description,
      autodialerBucketId: bucket.autodialerBucketId,
      autodialerBucketName: bucket.autodialerBucketName,
      salesChannels: bucket.salesChannels,
      priorities: bucket.priorities,
      uploadFrequency: bucket.uploadFrequency,
      opportunityStatus: bucket.opportunityStatus,
      isActive: bucket.isActive,
    });
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const request: AutodialerBucketConfigurationRequest = {
      ...values,
      lastModifiedUtc: new Date().toISOString(),
      ...(editingBucket ? { id: editingBucket.id } : {}),
    };

    try {
      setSaving(true);
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);

      if (editingBucket) {
        const result = await updateAutodialerBucket(token.accessToken, editingBucket.id, request);
        if (result === null) {
          notification.error({ message: 'Save failed', description: 'Server returned an error.' });
          return;
        }
        if (typeof result === 'object' && 'errors' in result) {
          notification.error({ message: 'Validation error', description: result.errors });
          return;
        }
        notification.success({ message: 'Bucket updated' });
      } else {
        const result = await createAutodialerBucket(token.accessToken, request);
        if (result === null) {
          notification.error({ message: 'Create failed', description: 'Server returned an error.' });
          return;
        }
        if ('errors' in result) {
          notification.error({ message: 'Validation error', description: result.errors });
          return;
        }
        notification.success({ message: 'Bucket created' });
      }

      setDrawerOpen(false);
      await load();
    } catch (e) {
      notification.error({ message: 'Save failed', description: String(e) });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const ok = await deleteAutodialerBucket(token.accessToken, id);
      if (ok) {
        notification.success({ message: 'Bucket deleted' });
        await load();
      } else {
        notification.error({ message: 'Delete failed' });
      }
    } catch (e) {
      notification.error({ message: 'Delete failed', description: String(e) });
    }
  };

  const columns: ColumnsType<AutodialerBucketConfiguration> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Bucket Name',
      dataIndex: 'autodialerBucketName',
      key: 'autodialerBucketName',
    },
    {
      title: 'Sales Channels',
      dataIndex: 'salesChannels',
      key: 'salesChannels',
      filters: SALES_CHANNEL_OPTIONS.map((o) => ({ text: o.label, value: o.value })),
      onFilter: (value, record) => record.salesChannels.includes(value as string),
      render: (channels: string[]) => (
        <Space size={[0, 4]} wrap>
          {channels.map((c) => <Tag key={c}>{c}</Tag>)}
        </Space>
      ),
    },
    {
      title: 'Priorities',
      dataIndex: 'priorities',
      key: 'priorities',
      render: (p: number[]) => p.join(', '),
    },
    {
      title: 'Frequency',
      dataIndex: 'uploadFrequency',
      key: 'uploadFrequency',
      filters: UPLOAD_FREQUENCY_OPTIONS.map((o) => ({ text: o.label, value: o.value })),
      onFilter: (value, record) => record.uploadFrequency === value,
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (value, record) => record.isActive === value,
      render: (v: boolean) => <Tag color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</Tag>,
    },
    {
      title: 'Last Modified',
      key: 'lastModified',
      render: (_, record) => (
        <span>
          {new Date(record.lastModifiedUtc).toLocaleString()}
          {record.modifiedBy ? ` — ${record.modifiedBy}` : ''}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete this bucket?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}>
            <Button icon={<DeleteOutlined />} size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const ifPrioRequired = () => {
    var isPrioRequired = true;
    if (uploadFrequencyWatcher === 'Manual') {
      isPrioRequired = false;
    }
    if (watchedBucketName && watchedBucketName.toLowerCase().includes('offtier')) {
      isPrioRequired = false;
    }
    return isPrioRequired;
  };

  const ifSalesChannelRequired = () => {
    var isSalesChannelRequired = true;
    if (uploadFrequencyWatcher === 'Manual') {
      isSalesChannelRequired = false;
    }

    return isSalesChannelRequired;
  };

  return (
    <div style={{ padding: 24, flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Autodialer Bucket Configurations
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Add Bucket
        </Button>
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={buckets}
        loading={loading}
        pagination={{ pageSize: 20 }}
        size="small"
      />

      <Drawer
        title={editingBucket ? 'Edit Bucket' : 'Add Bucket'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={520}
        footer={
          <Space>
            <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={handleSave}>
              Save
            </Button>
          </Space>
        }>
        <Form form={form} layout="vertical" requiredMark="optional">
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input />
          </Form.Item>
          <Form.Item
            name="autodialerBucketId"
            label="Autodialer Bucket ID"
            rules={[{ required: true, message: 'Autodialer Bucket ID is required' }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="autodialerBucketName"
            label="Autodialer Bucket Name"
            rules={[{ required: true, message: 'Autodialer Bucket Name is required' }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="salesChannels"
            label="Sales Channels"
            rules={[{ required: ifSalesChannelRequired(), message: 'At least one sales channel is required' }]}>
            <Select
              mode="multiple"
              options={SALES_CHANNEL_OPTIONS}
              placeholder="Select sales channels"
              onChange={(newChannels: string[]) => {
                if (!form.getFieldValue('isActive')) return;
                const newTaken = new Set<number>();
                for (const b of buckets) {
                  if (!b.isActive) continue;
                  if (editingBucket && b.id === editingBucket.id) continue;
                  if (b.salesChannels.some((ch) => newChannels.includes(ch))) {
                    b.priorities.forEach((p) => newTaken.add(p));
                  }
                }
                const current: number[] = form.getFieldValue('priorities') ?? [];
                form.setFieldValue('priorities', current.filter((p) => !newTaken.has(p)));
              }}
            />
          </Form.Item>
          <Form.Item
            name="priorities"
            label="Priorities"
            rules={[{ required: ifPrioRequired(), message: 'At least one priority is required' }]}>
            <Select mode="multiple" options={priorityOptions} placeholder="Select priorities" />
          </Form.Item>
          <Form.Item name="uploadFrequency" label="Upload Frequency" rules={[{ required: true }]}>
            <Select options={UPLOAD_FREQUENCY_OPTIONS} />
          </Form.Item>
          <Form.Item name="opportunityStatus" label="Opportunity Status">
            <Select options={OPPORTUNITY_STATUS_OPTIONS} placeholder="Not selected" allowClear />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export const AutodialerConfigPage = () => (
  <>
    <Header />
    <AutodialerBucketsConfig />
  </>
);
